import { useState, useMemo } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import DatePicker from '@/components/DatePicker';
import PlanningGrid from '@/components/PlanningGrid';
import PlanningWeekView from '@/components/PlanningWeekView';
import InterventionDrawer from '@/components/InterventionDrawer';
import {
  useInterventionsByDate,
  useInterventionsByWeek,
  useMechanics,
} from '@/hooks/useInterventions';
import { addDays, fromIsoDate, mondayOfIso, todayIso, toIsoDate } from '@/lib/dates';
import type { Intervention, TypeAgenda } from '@/lib/types';
import { TYPE_AGENDA_LABELS } from '@/lib/types';

const VALID_TYPES: TypeAgenda[] = ['rapide', 'mecanique', 'lavage'];

export default function PlanningPage() {
  const { type } = useParams<{ type?: string }>();
  const navigate = useNavigate();
  const [date, setDate] = useState(todayIso());
  const [viewMode, setViewMode] = useState<'day' | 'week'>('day');

  if (!type) return <Navigate to="/planning/service-rapide" replace />;

  const typeAgenda: TypeAgenda | null =
    type === 'service-rapide' ? 'rapide' : type === 'mecanique' ? 'mecanique' : type === 'lavage' ? 'lavage' : null;

  if (!typeAgenda) return <Navigate to="/planning/service-rapide" replace />;

  return (
    <PlanningContent
      typeAgenda={typeAgenda}
      date={date}
      viewMode={viewMode}
      onDateChange={setDate}
      onViewModeChange={setViewMode}
      onTypeChange={(t) => {
        const slug = t === 'rapide' ? 'service-rapide' : t;
        navigate(`/planning/${slug}`);
      }}
    />
  );
}

type ContentProps = {
  typeAgenda: TypeAgenda;
  date: string;
  viewMode: 'day' | 'week';
  onDateChange: (d: string) => void;
  onViewModeChange: (m: 'day' | 'week') => void;
  onTypeChange: (t: TypeAgenda) => void;
};

function PlanningContent({
  typeAgenda,
  date,
  viewMode,
  onDateChange,
  onViewModeChange,
  onTypeChange,
}: ContentProps) {
  const weekStart = useMemo(() => mondayOfIso(date), [date]);
  const dayQuery = useInterventionsByDate(date, typeAgenda);
  const weekQuery = useInterventionsByWeek(weekStart, typeAgenda);
  const activeQuery = viewMode === 'week' ? weekQuery : dayQuery;
  const mechanicsQuery = useMechanics();
  const [selectedInterventionId, setSelectedInterventionId] = useState<string | null>(null);

  const mechanics = useMemo(() => {
    if (!mechanicsQuery.data) return [];
    return mechanicsQuery.data
      .filter((m) => m.specialite === typeAgenda && m.actif)
      .sort((a, b) => a.ordreAffichage - b.ordreAffichage);
  }, [mechanicsQuery.data, typeAgenda]);

  const interventions = activeQuery.data ?? [];

  const totalRdv = interventions.length;
  const enCours = interventions.filter((i) => i.statut === 'EN_COURS').length;
  const totalDuration = interventions
    .filter((i) => i.statut !== 'ANNULE')
    .reduce((acc, i) => acc + i.dureeEstimeeMinutes, 0);
  const daysInPeriod = viewMode === 'week' ? 7 : 1;
  const capacityMinutes = mechanics.length * 8 * 60 * daysInPeriod;
  const occupationPct = capacityMinutes > 0 ? Math.round((totalDuration / capacityMinutes) * 100) : 0;

  const occupationLabel = viewMode === 'week' ? 'Charge semaine' : 'Charge atelier';
  const rdvLabel = viewMode === 'week' ? 'RDV semaine' : 'RDV jour';

  return (
    <div className="p-6 space-y-5">
      <header className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold text-text-primary">
            Planning {TYPE_AGENDA_LABELS[typeAgenda]}
          </h1>
          <p className="text-xs text-text-muted font-mono mt-0.5">
            Casablanca · vue {viewMode === 'week' ? 'semaine' : 'jour'}
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div
            className="inline-flex border border-border-light rounded-md overflow-hidden"
            role="tablist"
            aria-label="Mode d'affichage"
          >
            <button
              type="button"
              role="tab"
              aria-selected={viewMode === 'day'}
              onClick={() => onViewModeChange('day')}
              className={`px-3 h-8 text-xs font-medium transition-colors ${
                viewMode === 'day'
                  ? 'bg-ktm-orange-light text-ktm-orange'
                  : 'bg-surface-white text-text-secondary hover:bg-surface-hover'
              }`}
            >
              Jour
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={viewMode === 'week'}
              onClick={() => onViewModeChange('week')}
              className={`px-3 h-8 text-xs font-medium border-l border-border-light transition-colors ${
                viewMode === 'week'
                  ? 'bg-ktm-orange-light text-ktm-orange'
                  : 'bg-surface-white text-text-secondary hover:bg-surface-hover'
              }`}
            >
              Semaine
            </button>
          </div>
          {viewMode === 'day' && <DatePicker value={date} onChange={onDateChange} />}
        </div>
      </header>

      {/* Tabs type agenda */}
      <div className="flex items-center gap-1 border-b border-border-light">
        {VALID_TYPES.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => onTypeChange(t)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px ${
              typeAgenda === t
                ? 'border-ktm-orange text-ktm-orange'
                : 'border-transparent text-text-muted hover:text-text-secondary'
            }`}
          >
            {TYPE_AGENDA_LABELS[t]}
          </button>
        ))}
      </div>

      {/* KPIs compact */}
      <div className="flex items-center gap-6 text-sm">
        <Kpi label={occupationLabel} value={`${occupationPct}%`} accent />
        <Kpi label={rdvLabel} value={String(totalRdv)} />
        <Kpi label="En cours" value={String(enCours)} />
      </div>

      {/* Lavage : alerte si aucun mécano actif */}
      {typeAgenda === 'lavage' && mechanics.length === 0 && (
        <div className="px-4 py-3 rounded-md bg-ktm-orange-light border border-ktm-orange-border text-text-secondary text-sm">
          Mécanicien lavage à assigner —{' '}
          <button onClick={() => alert('Phase 7')} className="text-ktm-orange font-medium underline">
            paramétrer dans Administration
          </button>
        </div>
      )}

      {/* Grid */}
      {activeQuery.isError || mechanicsQuery.isError ? (
        <div
          role="alert"
          className="py-12 px-4 text-center rounded-md bg-status-annule-bg border border-status-annule-border text-status-annule-text text-sm"
        >
          <p className="font-semibold">Erreur de chargement du planning</p>
          <p className="mt-1 text-text-secondary">
            Les créneaux affichés peuvent être incomplets ou périmés. Ceci n'est PAS un atelier vide.
          </p>
          <button
            type="button"
            onClick={() => {
              void activeQuery.refetch();
              void mechanicsQuery.refetch();
            }}
            className="mt-3 px-3 h-8 text-xs font-medium rounded-md bg-ktm-orange text-text-on-orange"
          >
            Réessayer
          </button>
        </div>
      ) : activeQuery.isLoading || mechanicsQuery.isLoading ? (
        <div className="py-16 text-center text-text-muted text-sm">Chargement…</div>
      ) : mechanics.length === 0 ? (
        <div className="py-16 text-center text-text-muted text-sm">
          Aucun mécanicien actif pour ce type d'agenda.
        </div>
      ) : viewMode === 'week' ? (
        <PlanningWeekView
          weekStart={weekStart}
          mechanics={mechanics}
          interventions={interventions}
          onPrevWeek={() => onDateChange(toIsoDate(addDays(fromIsoDate(weekStart), -7)))}
          onNextWeek={() => onDateChange(toIsoDate(addDays(fromIsoDate(weekStart), 7)))}
          onTodayWeek={() => onDateChange(todayIso())}
          onClickIntervention={(i: Intervention) => setSelectedInterventionId(i.id)}
          onDayHeaderClick={(iso) => {
            onDateChange(iso);
            onViewModeChange('day');
          }}
        />
      ) : (
        <PlanningGrid
          mechanics={mechanics}
          interventions={interventions}
          date={date}
          onClickIntervention={(i: Intervention) => setSelectedInterventionId(i.id)}
        />
      )}

      <InterventionDrawer
        interventionId={selectedInterventionId}
        onClose={() => setSelectedInterventionId(null)}
      />


      {/* Legend */}
      <footer className="flex items-center gap-4 text-xs text-text-muted pt-4 border-t border-border-light flex-wrap">
        <LegendDot color="#2563EB" label="Confirmé" />
        <LegendDot color="#6B7280" label="En attente" />
        <LegendDot color="#D97706" label="En cours" />
        <LegendDot color="#EA580C" label="Attente pièces" />
        <LegendDot color="#16A34A" label="Terminé" />
        <LegendDot color="#374151" label="Livré" />
        <LegendDot color="#DC2626" label="Annulé" />
      </footer>
    </div>
  );
}

function Kpi({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className="flex items-baseline gap-2">
      <span className="text-xs text-text-muted uppercase tracking-wider">{label}</span>
      <span
        className={`font-mono font-semibold ${accent ? 'text-ktm-orange text-lg' : 'text-text-primary'}`}
      >
        {value}
      </span>
    </div>
  );
}

function LegendDot({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
      <span className="uppercase tracking-wide">{label}</span>
    </span>
  );
}
